/**
 * "Ticket erstellen" (#1170, epic #1167): one floating panel for bug reports
 * and feature requests, replacing the modal wizard and the feature-request
 * dialog.
 *
 * - Non-blocking: no backdrop, no focus trap, an outside click does nothing —
 *   the person keeps looking at and using what they are reporting. Escape or
 *   the close button hide it; the draft stays (this component stays mounted
 *   for the session), only a successful submit clears it.
 * - Opened by window events (`OPEN_BUG_REPORT_EVENT`,
 *   `OPEN_FEATURE_REQUEST_EVENT`), which preset the type.
 * - Context (system, window, route, version, browser, client, time) is read
 *   at every opening and shown as chips; "anhängen" decides whether it is sent.
 * - Screenshots and recordings hide the panel while they capture; a
 *   screenshot then opens the marking view.
 * - After a submit: `BUG_REPORT_SUBMITTED_EVENT` with `detail.reportId` (so
 *   "Meine Tickets" lists and opens it) and a success view with "Meine
 *   Tickets öffnen", which the host resolves (Cortex window or `/tickets`).
 * - While typing (#1172): similar tickets for the title (debounced; non-admins
 *   only their own), "Öffnen" through the same host callback, "Ergänzen" turns
 *   the draft into a reply to that ticket (`POST /{id}/messages`, no new
 *   ticket). A type/category hint after the description — applied only by a
 *   click, never for what the person chose themselves.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import {
  Bug,
  Camera,
  CheckCircle2,
  FileText,
  Lightbulb,
  Loader2,
  Maximize2,
  Minimize2,
  Paperclip,
  PenLine,
  Square,
  Video,
  X,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { toast } from '@/components/ui/use-toast'
import { env } from '@/lib/env'
import { logError } from '@/lib/logger'
import {
  BUG_REPORT_SUBMITTED_EVENT,
  BugReportApiError,
  DOCUMENT_ACCEPT,
  OPEN_BUG_REPORT_EVENT,
  OPEN_FEATURE_REQUEST_EVENT,
  bugReportService,
  categoriesForType,
  isAllowedAttachment,
  type BugReportSystem,
  type SimilarTicket,
  type TicketCategory,
  type TicketContext,
  type TicketType,
} from '@/services/bug-report-service'
import {
  EASE_STANDARD,
  FLOAT_OFFSET_PX,
  FLOAT_TRANSITION,
  FLOATING_SURFACE,
  FOCUS_RING,
  INSTANT_TRANSITION,
  STATUS,
  readable,
} from '../notifications/notificationStyles'
import { loadClientName } from './clientName'
import type { MarkupShape } from './markup'
import { ScreenshotMarkup } from './ScreenshotMarkup'
import { buildTicketContext, contextChips, CONTEXT_FIELD_LIMITS, type TicketContextSource } from './ticketContext'
import { AppendTargetCard, SimilarTicketsBox, SuggestionHintRow } from './TicketAssistViews'
import { openingType } from './ticketTypes'
import { useAllowedTicketTypes } from '@/hooks/useAllowedTicketTypes'
import { SUGGESTION_TITLE_ONLY_CHARS, SIMILAR_MIN_CHARS, hintFor, useSimilarTickets, useTicketSuggestion } from './ticketAssist'
import {
  readTicketPanelMaximized,
  storeTicketPanelMaximized,
  ticketPanelGeometry,
  type TicketPanelAnchor,
} from './ticketPanelGeometry'
import { MAX_RECORDING_SECONDS, useScreenCapture, type CaptureError } from './useScreenCapture'

export interface TicketPanelProps {
  anchor: TicketPanelAnchor
  /** The `system` the BFF requires; the panel no longer asks for it. */
  system: BugReportSystem
  /** Where the person is, read at every opening. */
  readContextSource: () => TicketContextSource
  /** "Meine Tickets öffnen" after a submit, and "Öffnen" on an own similar ticket. */
  onOpenMyTickets: (reportId: string) => void
  /**
   * "Öffnen" on someone else's similar ticket (only admins are shown those):
   * the admin view. Without it the host has no view for such a ticket and the
   * button is left out (#1191).
   */
  onOpenOthersTicket?: (reportId: string) => void
}

type AttachmentKind = 'screenshot' | 'recording' | 'file'

interface PanelAttachment {
  id: string
  kind: AttachmentKind
  /** What is uploaded; for a screenshot the marked PNG. */
  file: File
  /** Screenshots: the capture without marks, and the marks on it. */
  original?: Blob
  shapes?: MarkupShape[]
}

type View =
  | { name: 'form' }
  | { name: 'markup'; attachmentId: string }
  | { name: 'success'; reportId: string; appended: boolean }

const RESIZE_TRANSITION = `width 180ms cubic-bezier(${EASE_STANDARD.join(',')}), height 180ms cubic-bezier(${EASE_STANDARD.join(',')})`
const ICON_BUTTON = `h-8 w-8 flex items-center justify-center rounded-md text-[var(--text-secondary)] hover:text-[var(--text-color)] hover:bg-[var(--menu-item-hover)] ${FOCUS_RING}`
const ACCENT_TINT = 'color-mix(in srgb, var(--theme-interactive-color) 14%, transparent)'
const FILE_ACCEPT = ['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'video/mp4', 'video/webm', DOCUMENT_ACCEPT].join(',')
const SUBMIT_ERROR_CODES = new Set([
  'BUG_REPORT_MAX_FILES_EXCEEDED',
  'BUG_REPORT_FILE_TOO_LARGE',
  'BUG_REPORT_INVALID_FILE_TYPE',
  'BUG_REPORT_NOT_AUTHENTICATED',
  'BUG_REPORT_UPLOAD_FAILED',
])

let attachmentSeq = 0
const nextAttachmentId = () => `att-${++attachmentSeq}`

function stamp(): string {
  return new Date().toISOString().replace(/[:.]/g, '-')
}

function AttachmentThumb({ attachment }: { attachment: PanelAttachment }) {
  const isImage = attachment.file.type.startsWith('image/')
  const [url, setUrl] = useState<string | null>(null)
  useEffect(() => {
    if (!isImage) return
    const objectUrl = URL.createObjectURL(attachment.file)
    setUrl(objectUrl)
    return () => URL.revokeObjectURL(objectUrl)
  }, [attachment.file, isImage])
  const Icon = attachment.kind === 'recording' || attachment.file.type.startsWith('video/') ? Video : FileText
  return (
    <span className="flex h-[46px] w-[72px] shrink-0 items-center justify-center overflow-hidden rounded border border-[var(--window-border-color)] bg-[var(--menu-item-hover)]">
      {url ? <img src={url} alt="" className="h-full w-full object-cover" /> : <Icon className="h-5 w-5 text-[var(--text-secondary)]" aria-hidden />}
    </span>
  )
}

export function TicketPanel({ anchor, system, readContextSource, onOpenMyTickets, onOpenOthersTicket }: TicketPanelProps) {
  const { t, i18n } = useTranslation()
  const reduceMotion = useReducedMotion()
  const locale = i18n.language?.startsWith('en') ? 'en-GB' : 'de-DE'

  const allowedTypes = useAllowedTicketTypes()
  const bothTypes = allowedTypes.bug && allowedTypes.feature
  const [open, setOpen] = useState(false)
  const [view, setView] = useState<View>({ name: 'form' })
  const [maximized, setMaximized] = useState(readTicketPanelMaximized)

  // ── Draft (kept while the panel is closed) ────────────────────────────────
  const [type, setType] = useState<TicketType>('bug')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [category, setCategory] = useState<TicketCategory | ''>('')
  const [attachments, setAttachments] = useState<PanelAttachment[]>([])
  const [includeContext, setIncludeContext] = useState(true)
  const [context, setContext] = useState<TicketContext>({})
  const [showErrors, setShowErrors] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  // #1172: the ticket the draft is appended to, and what the person chose themselves.
  const [appendTarget, setAppendTarget] = useState<SimilarTicket | null>(null)
  const [typeTouched, setTypeTouched] = useState(false)
  const [categoryTouched, setCategoryTouched] = useState(false)
  const [suggestionDismissed, setSuggestionDismissed] = useState(false)

  const panelRef = useRef<HTMLDivElement>(null)
  const titleRef = useRef<HTMLInputElement>(null)
  const descriptionRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const returnFocusRef = useRef<HTMLElement | null>(null)
  const readSourceRef = useRef(readContextSource)
  readSourceRef.current = readContextSource

  const maxAttachments = bugReportService.maxAttachments
  const maxFileSizeMb = bugReportService.maxFileSizeBytes / (1024 * 1024)
  const full = attachments.length >= maxAttachments

  // ── Opening ───────────────────────────────────────────────────────────────
  const captureContext = useCallback(() => {
    const captured = buildTicketContext({
      source: readSourceRef.current(),
      appVersion: env.appVersion,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    })
    setContext(captured)
    void loadClientName().then((name) => {
      if (!name) return
      setContext((current) =>
        current.captured_at === captured.captured_at
          ? { ...current, client_name: name.slice(0, CONTEXT_FIELD_LIMITS.client_name) }
          : current,
      )
    })
  }, [])

  const openWith = useCallback(
    (requested: TicketType) => {
      // #1544: a type the roles switched off opens as the other one, or not at all.
      const preset = openingType(requested, allowedTypes)
      if (!preset) return
      const active = document.activeElement
      if (!open) returnFocusRef.current = active instanceof HTMLElement && active !== document.body ? active : null
      setView((current) => (current.name === 'success' ? { name: 'form' } : current))
      setType(preset)
      setCategory((current) => (current && !categoriesForType(preset).includes(current) ? '' : current))
      captureContext()
      setOpen(true)
    },
    [open, captureContext, allowedTypes],
  )

  useEffect(() => {
    const openBug = () => openWith('bug')
    const openFeature = () => openWith('feature')
    window.addEventListener(OPEN_BUG_REPORT_EVENT, openBug)
    window.addEventListener(OPEN_FEATURE_REQUEST_EVENT, openFeature)
    return () => {
      window.removeEventListener(OPEN_BUG_REPORT_EVENT, openBug)
      window.removeEventListener(OPEN_FEATURE_REQUEST_EVENT, openFeature)
    }
  }, [openWith])

  // The title field gets the focus when the form shows (after the open animation mounted it).
  useEffect(() => {
    if (!open || view.name !== 'form') return
    const frame = requestAnimationFrame(() => titleRef.current?.focus({ preventScroll: true }))
    return () => cancelAnimationFrame(frame)
  }, [open, view.name])

  // ── Capture ───────────────────────────────────────────────────────────────
  const addAttachment = useCallback(
    (attachment: PanelAttachment): boolean => {
      if (attachments.length >= maxAttachments) {
        toast({
          variant: 'destructive',
          title: t('bugReport.validation.tooManyFilesTitle'),
          description: t('bugReport.validation.tooManyFiles', { max: maxAttachments }),
        })
        return false
      }
      if (attachment.file.size > bugReportService.maxFileSizeBytes) {
        toast({
          variant: 'destructive',
          title: t('bugReport.validation.fileTooLargeTitle'),
          description: t('bugReport.validation.fileTooLarge', { name: attachment.file.name, max: maxFileSizeMb }),
        })
        return false
      }
      setAttachments((current) => [...current, attachment])
      return true
    },
    [attachments.length, maxAttachments, maxFileSizeMb, t],
  )

  const capture = useScreenCapture({
    onScreenshot: (png) => {
      const attachment: PanelAttachment = {
        id: nextAttachmentId(),
        kind: 'screenshot',
        file: new File([png], `screenshot-${stamp()}.png`, { type: 'image/png' }),
        original: png,
        shapes: [],
      }
      if (addAttachment(attachment)) setView({ name: 'markup', attachmentId: attachment.id })
    },
    onRecording: (webm) => {
      addAttachment({
        id: nextAttachmentId(),
        kind: 'recording',
        file: new File([webm], `recording-${stamp()}.webm`, { type: 'video/webm' }),
      })
    },
    onError: (error: CaptureError) => {
      toast({ variant: 'destructive', title: t(`ticketPanel.capture.${error}`) })
    },
  })
  const capturing = capture.active

  // Tooltips and menus (Radix portals) hide during a capture too, as in the old wizard.
  useEffect(() => {
    if (!capturing) return
    document.body.classList.add('cdb-screen-capturing')
    return () => document.body.classList.remove('cdb-screen-capturing')
  }, [capturing])

  // ── Closing ───────────────────────────────────────────────────────────────
  const close = useCallback(() => {
    if (capturing || submitting) return
    setOpen(false)
    setView((current) => (current.name === 'markup' ? { name: 'form' } : current))
    const target = returnFocusRef.current
    if (target?.isConnected) target.focus({ preventScroll: true })
  }, [capturing, submitting])

  const resetDraft = () => {
    setTitle('')
    setDescription('')
    setCategory('')
    setAttachments([])
    setIncludeContext(true)
    setShowErrors(false)
    setAppendTarget(null)
    setTypeTouched(false)
    setCategoryTouched(false)
    setSuggestionDismissed(false)
    resetSuggestion()
  }

  // Escape closes wherever the focus is; inside the marking view it leaves that
  // view first. A key a menu or another dialog handled is theirs.
  const viewRef = useRef(view)
  viewRef.current = view
  useEffect(() => {
    if (!open) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return
      const target = event.target instanceof Element ? event.target : null
      if (target?.closest('[role="menu"], [role="listbox"]')) return
      const otherDialog = target?.closest('[role="dialog"], [role="alertdialog"]')
      if (otherDialog && otherDialog !== panelRef.current) return
      if (viewRef.current.name === 'markup') {
        setView({ name: 'form' })
        return
      }
      close()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [open, close])

  const toggleMaximized = () => {
    const next = !maximized
    setMaximized(next)
    storeTicketPanelMaximized(next)
  }

  // ── Form ──────────────────────────────────────────────────────────────────
  const chooseType = (next: TicketType) => {
    setType(next)
    setTypeTouched(true)
    if (category && !categoriesForType(next).includes(category)) setCategory('')
  }

  // ── Help while typing (#1172) ─────────────────────────────────────────────
  const similar = useSimilarTickets(title, type, open && view.name === 'form' && !appendTarget)
  const { suggestion, request: requestSuggestion, reset: resetSuggestion } = useTicketSuggestion()
  const hint =
    appendTarget || suggestionDismissed
      ? null
      : hintFor(suggestion, { type, category, typeTouched, categoryTouched, allowedTypes })

  const askForSuggestion = (trigger: 'title' | 'description') => {
    if (appendTarget || (typeTouched && categoryTouched)) return
    const titleLength = title.trim().length
    if (titleLength < SIMILAR_MIN_CHARS) return
    if (trigger === 'title' && titleLength < SUGGESTION_TITLE_ONLY_CHARS) return
    requestSuggestion(title, description)
  }

  const applyHint = () => {
    if (!hint) return
    if (hint.kind === 'type') {
      setType(hint.type)
      setCategory(hint.category ?? (category && categoriesForType(hint.type).includes(category) ? category : ''))
    } else {
      setCategory(hint.category)
    }
    setTypeTouched(true)
    setCategoryTouched(true)
  }

  const canOpenSimilar = (ticket: SimilarTicket) => ticket.own || onOpenOthersTicket !== undefined
  const openSimilar = (ticket: SimilarTicket) =>
    ticket.own ? onOpenMyTickets(ticket.id) : onOpenOthersTicket?.(ticket.id)

  const startAppend = (ticket: SimilarTicket) => {
    setAppendTarget(ticket)
    setShowErrors(false)
    requestAnimationFrame(() => descriptionRef.current?.focus({ preventScroll: true }))
  }

  const titleMissing = title.trim().length === 0
  const descriptionMissing = description.trim().length === 0

  const onFilesPicked = (event: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(event.target.files ?? [])
    event.target.value = ''
    let room = maxAttachments - attachments.length
    for (const file of picked) {
      if (!isAllowedAttachment(file)) {
        toast({
          variant: 'destructive',
          title: t('bugReport.validation.invalidTypeTitle'),
          description: t('bugReport.validation.invalidType', { name: file.name }),
        })
        continue
      }
      if (room <= 0 || !addAttachment({ id: nextAttachmentId(), kind: 'file', file })) break
      room -= 1
    }
  }

  const removeAttachment = (id: string) => setAttachments((current) => current.filter((a) => a.id !== id))

  const applyMarkup = (id: string, shapes: MarkupShape[], marked: Blob) => {
    setAttachments((current) =>
      current.map((a) =>
        a.id === id ? { ...a, shapes, file: new File([marked], a.file.name, { type: 'image/png' }) } : a,
      ),
    )
    setView({ name: 'form' })
  }

  const submitAppend = async (target: SimilarTicket) => {
    if (descriptionMissing) {
      setShowErrors(true)
      descriptionRef.current?.focus()
      return
    }
    setSubmitting(true)
    try {
      await bugReportService.postMessage(target.id, {
        body: description,
        files: attachments.map((a) => a.file),
      })
      window.dispatchEvent(new CustomEvent(BUG_REPORT_SUBMITTED_EVENT, { detail: { reportId: target.id } }))
      resetDraft()
      setView({ name: 'success', reportId: target.id, appended: true })
    } catch (error) {
      const closed = error instanceof BugReportApiError && error.code === 'report_closed'
      const code = error instanceof Error && SUBMIT_ERROR_CODES.has(error.message) ? error.message : null
      logError(error as Error, { context: 'TicketPanel.append', attachments: attachments.length })
      // A ticket closed in the meantime takes no reply: back to a new ticket, draft kept.
      if (closed) setAppendTarget(null)
      toast({
        variant: 'destructive',
        title: t('ticketPanel.append.errorTitle'),
        description: closed
          ? t('ticketPanel.append.closed')
          : code
            ? t(`bugReport.errors.${code}`)
            : t('ticketPanel.errors.submit'),
      })
    } finally {
      setSubmitting(false)
    }
  }

  const submit = async () => {
    if (appendTarget) {
      await submitAppend(appendTarget)
      return
    }
    if (titleMissing || descriptionMissing) {
      setShowErrors(true)
      ;(titleMissing ? titleRef : descriptionRef).current?.focus()
      return
    }
    setSubmitting(true)
    try {
      const report = await bugReportService.createReport({
        title,
        description,
        type,
        // Optional in the panel; `other` exists for both types (#1168).
        category: category || 'other',
        system,
        ...(includeContext ? { context } : {}),
        files: attachments.map((a) => a.file),
      })
      window.dispatchEvent(new CustomEvent(BUG_REPORT_SUBMITTED_EVENT, { detail: { reportId: report.id } }))
      resetDraft()
      setView({ name: 'success', reportId: report.id, appended: false })
    } catch (error) {
      const code = error instanceof Error && SUBMIT_ERROR_CODES.has(error.message) ? error.message : null
      const typeDisabled = error instanceof BugReportApiError && error.code === 'ticket_type_disabled'
      logError(error as Error, { context: 'TicketPanel.submit', type, attachments: attachments.length, withContext: includeContext })
      toast({
        variant: 'destructive',
        title: t('ticketPanel.errors.submitTitle'),
        description: typeDisabled
          ? t(`ticketPanel.errors.typeDisabled.${type}`)
          : code
            ? t(`bugReport.errors.${code}`)
            : t('ticketPanel.errors.submit'),
      })
    } finally {
      setSubmitting(false)
    }
  }

  const chips = useMemo(
    () =>
      contextChips(context, {
        version: (version) => t('ticketPanel.context.version', { version }),
        date: (iso) =>
          new Date(iso).toLocaleString(locale, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }),
      }),
    [context, locale, t],
  )

  const markupTarget = view.name === 'markup' ? attachments.find((a) => a.id === view.attachmentId) : undefined

  // ── Render ────────────────────────────────────────────────────────────────
  const captureBar =
    capture.phase === 'ready' || capture.phase === 'recording'
      ? createPortal(
          <div
            role="status"
            data-testid="ticket-panel-capture-bar"
            className="fixed bottom-[70px] left-1/2 z-[15010] flex -translate-x-1/2 items-center gap-3 rounded-full border border-[var(--window-border-color)] bg-[var(--window-background)] px-4 py-2 text-sm text-[var(--text-color)]"
            style={FLOATING_SURFACE}
          >
            <span className="h-2.5 w-2.5 shrink-0 animate-pulse rounded-full" style={{ background: capture.phase === 'recording' ? STATUS.danger : 'var(--theme-interactive-color)' }} aria-hidden />
            {capture.phase === 'ready' ? (
              <>
                <span>{t('ticketPanel.capture.ready')}</span>
                <Button type="button" size="sm" onClick={() => void capture.takeScreenshot()} data-testid="ticket-panel-capture-now">
                  <Camera className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                  {t('ticketPanel.capture.now')}
                </Button>
              </>
            ) : (
              <>
                <span>{t('ticketPanel.capture.recording')}</span>
                <span className="tabular-nums text-[var(--text-secondary)]">
                  {t('ticketPanel.capture.secondsLeft', { seconds: capture.secondsLeft, max: MAX_RECORDING_SECONDS })}
                </span>
                <Button type="button" size="sm" onClick={() => void capture.stopRecording()} data-testid="ticket-panel-capture-stop">
                  <Square className="mr-1.5 h-3 w-3 fill-current" aria-hidden />
                  {t('ticketPanel.capture.stop')}
                </Button>
              </>
            )}
            <Button type="button" size="sm" variant="ghost" onClick={capture.cancel}>
              {t('common.cancel')}
            </Button>
          </div>,
          document.body,
        )
      : null

  const typeOption = (value: TicketType, Icon: typeof Bug, color: string) => {
    const pressed = type === value
    return (
      <button
        type="button"
        aria-pressed={pressed}
        data-testid={`ticket-panel-type-${value}`}
        onClick={() => chooseType(value)}
        className={`flex h-[38px] min-w-0 items-center justify-center gap-1.5 px-2 text-[13px] ${FOCUS_RING} ${
          pressed ? 'font-medium text-[var(--text-color)]' : 'text-[var(--text-secondary)] hover:bg-[var(--menu-item-hover)]'
        }`}
        style={pressed ? { background: ACCENT_TINT } : undefined}
      >
        <Icon className="h-[15px] w-[15px] shrink-0" style={{ color: readable(color) }} aria-hidden />
        <span className="truncate">{t(`ticketPanel.type.${value}`)}</span>
      </button>
    )
  }

  const form = (
    <>
      <div className="flex min-h-0 flex-1 flex-col gap-3.5 overflow-y-auto px-[18px] pb-3.5">
        {appendTarget ? (
          <AppendTargetCard ticket={appendTarget} onCancel={() => setAppendTarget(null)} />
        ) : (
          <>
            {/* Only one type allowed (#1544): no choice to offer, the panel opened with it. */}
            {bothTypes && (
              <div
                role="group"
                aria-label={t('ticketPanel.type.label')}
                className="grid shrink-0 grid-cols-2 divide-x divide-[var(--window-border-color)] overflow-hidden rounded-lg border border-[var(--window-border-color)]"
              >
                {typeOption('bug', Bug, STATUS.danger)}
                {typeOption('feature', Lightbulb, STATUS.warning)}
              </div>
            )}

            <div className="flex flex-col gap-1.5">
              <label htmlFor="ticket-panel-title" className="text-[13px] font-medium text-[var(--text-color)]">
                {t(`ticketPanel.form.titleLabel.${type}`)}
              </label>
              <Input
                id="ticket-panel-title"
                ref={titleRef}
                data-testid="ticket-panel-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                onBlur={() => askForSuggestion('title')}
                placeholder={t(`ticketPanel.form.titlePlaceholder.${type}`)}
                maxLength={100}
                required
                aria-invalid={showErrors && titleMissing}
                aria-describedby={showErrors && titleMissing ? 'ticket-panel-title-error' : undefined}
                disabled={submitting}
              />
              {showErrors && titleMissing && (
                <p id="ticket-panel-title-error" className="text-xs" style={{ color: readable(STATUS.danger) }}>
                  {t('ticketPanel.validation.title')}
                </p>
              )}
            </div>

            {similar.length > 0 && <SimilarTicketsBox tickets={similar} canOpen={canOpenSimilar} onOpen={openSimilar} onAppend={startAppend} />}
          </>
        )}

        <div className="flex flex-col gap-1.5">
          <label htmlFor="ticket-panel-description" className="text-[13px] font-medium text-[var(--text-color)]">
            {appendTarget ? t('ticketPanel.append.descriptionLabel') : t('ticketPanel.form.descriptionLabel')}
          </label>
          <Textarea
            id="ticket-panel-description"
            ref={descriptionRef}
            data-testid="ticket-panel-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            onBlur={() => askForSuggestion('description')}
            placeholder={
              appendTarget ? t('ticketPanel.append.descriptionPlaceholder') : t(`ticketPanel.form.descriptionPlaceholder.${type}`)
            }
            rows={4}
            required
            aria-invalid={showErrors && descriptionMissing}
            aria-describedby={showErrors && descriptionMissing ? 'ticket-panel-description-error' : undefined}
            disabled={submitting}
            className="resize-none"
          />
          {showErrors && descriptionMissing && (
            <p id="ticket-panel-description-error" className="text-xs" style={{ color: readable(STATUS.danger) }}>
              {t('ticketPanel.validation.description')}
            </p>
          )}
        </div>

        {hint && (
          <SuggestionHintRow hint={hint} onApply={applyHint} onDismiss={() => setSuggestionDismissed(true)} />
        )}

        {!appendTarget && (
          <div className="flex flex-col gap-1.5">
            <label htmlFor="ticket-panel-category" className="text-[13px] font-medium text-[var(--text-color)]">
              {t('ticketPanel.form.categoryLabel')}{' '}
              <span className="font-normal text-[var(--text-secondary)]">({t('ticketPanel.form.optional')})</span>
            </label>
            <Select
              id="ticket-panel-category"
              data-testid="ticket-panel-category"
              value={category}
              onValueChange={(value) => {
                setCategory(value as TicketCategory | '')
                setCategoryTouched(true)
              }}
              options={[
                { value: '', label: t('ticketPanel.form.categoryNone') },
                ...categoriesForType(type).map((value) => ({ value, label: t(`bugReport.category.${value}`) })),
              ]}
              optionTestIdPrefix="ticket-panel-category-"
              disabled={submitting}
            />
          </div>
        )}

        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <span id="ticket-panel-attachments-label" className="text-[13px] font-medium text-[var(--text-color)]">
              {t('ticketPanel.attachments.label')}
            </span>
            <span className="text-xs text-[var(--text-secondary)]">
              {t('ticketPanel.attachments.count', { used: attachments.length, max: maxAttachments })}
            </span>
          </div>
          <div role="group" aria-labelledby="ticket-panel-attachments-label" className="grid grid-cols-3 gap-2">
            {(
              [
                ['screenshot', Camera, () => void capture.startScreenshot()],
                ['recording', Video, () => void capture.startRecording()],
                ['file', Paperclip, () => fileInputRef.current?.click()],
              ] as const
            ).map(([key, Icon, onClick]) => (
              <button
                key={key}
                type="button"
                onClick={onClick}
                disabled={full || submitting || capturing}
                data-testid={`ticket-panel-${key}`}
                className={`flex h-9 min-w-0 items-center justify-center gap-1.5 rounded-lg border border-dashed border-[var(--window-border-color)] px-2 text-[13px] text-[var(--text-color)] hover:bg-[var(--menu-item-hover)] disabled:pointer-events-none disabled:opacity-50 ${FOCUS_RING}`}
              >
                <Icon className="h-3.5 w-3.5 shrink-0 text-[var(--text-secondary)]" aria-hidden />
                <span className="truncate">{t(`ticketPanel.attachments.${key}`)}</span>
              </button>
            ))}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={FILE_ACCEPT}
            onChange={onFilesPicked}
            className="hidden"
            data-testid="ticket-panel-file-input"
            tabIndex={-1}
            aria-hidden
          />
          {attachments.length > 0 && (
            <ul className="flex flex-col gap-2" data-testid="ticket-panel-attachments">
              {attachments.map((attachment) => {
                const marked = (attachment.shapes?.length ?? 0) > 0
                return (
                  <li
                    key={attachment.id}
                    className="flex items-center gap-2.5 rounded-lg border border-[var(--window-border-color)] p-2"
                    data-testid="ticket-panel-attachment"
                    data-kind={attachment.kind}
                  >
                    <AttachmentThumb attachment={attachment} />
                    <span className="min-w-0 flex-1 text-[13px]">
                      <span className="block truncate text-[var(--text-color)]">
                        {attachment.kind === 'file'
                          ? attachment.file.name
                          : t(`ticketPanel.attachments.kind.${attachment.kind}${marked ? 'Marked' : ''}`)}
                      </span>
                      <span className="block truncate text-xs text-[var(--text-secondary)]">
                        {attachment.kind === 'file'
                          ? `${(attachment.file.size / (1024 * 1024)).toFixed(2)} MB`
                          : t('ticketPanel.attachments.capturedHint')}
                      </span>
                    </span>
                    {attachment.kind === 'screenshot' && (
                      <button
                        type="button"
                        onClick={() => setView({ name: 'markup', attachmentId: attachment.id })}
                        className={`flex h-7 items-center gap-1 rounded-md px-2 text-xs text-[var(--text-secondary)] hover:bg-[var(--menu-item-hover)] hover:text-[var(--text-color)] ${FOCUS_RING}`}
                        data-testid="ticket-panel-attachment-mark"
                      >
                        <PenLine className="h-3.5 w-3.5" aria-hidden />
                        {t('ticketPanel.attachments.mark')}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => removeAttachment(attachment.id)}
                      aria-label={t('ticketPanel.attachments.remove', {
                        name: attachment.kind === 'file' ? attachment.file.name : t(`ticketPanel.attachments.kind.${attachment.kind}`),
                      })}
                      className={`${ICON_BUTTON} h-7 w-7`}
                      disabled={submitting}
                    >
                      <X className="h-4 w-4" aria-hidden />
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
        </div>

        {!appendTarget && (
          <section aria-labelledby="ticket-panel-context-title" className="flex flex-col gap-2">
            <div className="flex items-center justify-between gap-2">
              <h3 id="ticket-panel-context-title" className="text-[13px] font-medium text-[var(--text-color)]">
                {t('ticketPanel.context.title')}
              </h3>
              <label className="flex cursor-pointer items-center gap-1.5 text-xs text-[var(--text-secondary)]">
                <Checkbox
                  checked={includeContext}
                  onCheckedChange={(checked) => setIncludeContext(checked === true)}
                  data-testid="ticket-panel-context-toggle"
                  aria-describedby="ticket-panel-context-title"
                />
                {t('ticketPanel.context.attach')}
              </label>
            </div>
            <ul
              data-testid="ticket-panel-context"
              data-included={includeContext}
              aria-label={t('ticketPanel.context.title')}
              className={`flex flex-wrap gap-1.5 transition-opacity ${includeContext ? '' : 'opacity-50'}`}
            >
              {chips.map((chip) => (
                <li
                  key={chip.key}
                  data-context-key={chip.key}
                  title={chip.label}
                  className={`max-w-full truncate rounded-full border border-[var(--window-border-color)] bg-[var(--menu-item-hover)] px-2.5 py-0.5 text-xs text-[var(--text-color)] ${includeContext ? '' : 'line-through'}`}
                >
                  {chip.label}
                </li>
              ))}
            </ul>
            {!includeContext && <p className="text-xs text-[var(--text-secondary)]">{t('ticketPanel.context.excluded')}</p>}
          </section>
        )}
      </div>

      <footer className="flex shrink-0 items-center justify-between gap-3 border-t border-[var(--window-border-color)] px-[18px] pb-4 pt-3">
        <span className="text-xs text-[var(--text-secondary)]">{t('ticketPanel.footer.replyNotice')}</span>
        <Button type="button" onClick={() => void submit()} disabled={submitting || capturing} data-testid="ticket-panel-submit">
          {submitting && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden />}
          {submitting
            ? t('ticketPanel.footer.submitting')
            : appendTarget
              ? t('ticketPanel.append.submit')
              : t('ticketPanel.footer.submit')}
        </Button>
      </footer>
    </>
  )

  const success =
    view.name === 'success' ? (
      <div data-testid="ticket-panel-success" role="status" className="flex flex-1 flex-col items-center justify-center gap-3 px-8 pb-10 text-center">
        <CheckCircle2 className="h-10 w-10" style={{ color: readable(STATUS.success) }} aria-hidden />
        <p className="text-base font-semibold text-[var(--text-color)]">
          {view.appended ? t('ticketPanel.append.successTitle') : t('ticketPanel.success.title')}
        </p>
        <p className="text-sm text-[var(--text-secondary)]">
          {view.appended ? t('ticketPanel.append.successText') : t('ticketPanel.success.text')}
        </p>
        <div className="mt-2 flex flex-wrap justify-center gap-2">
          <Button
            type="button"
            data-testid="ticket-panel-open-tickets"
            onClick={() => {
              onOpenMyTickets(view.reportId)
              setView({ name: 'form' })
              setOpen(false)
            }}
          >
            {view.appended ? t('ticketPanel.append.successOpen') : t('ticketPanel.success.open')}
          </Button>
          <Button type="button" variant="outline" onClick={() => setView({ name: 'form' })} data-testid="ticket-panel-another">
            {t('ticketPanel.success.another')}
          </Button>
        </div>
      </div>
    ) : null

  return (
    <>
      {captureBar}
      <AnimatePresence>
        {open && (
          <motion.div
            ref={panelRef}
            role="dialog"
            aria-modal="false"
            aria-labelledby="ticket-panel-heading"
            aria-hidden={capturing || undefined}
            data-testid="ticket-panel"
            data-capturing={capturing}
            data-maximized={maximized}
            data-anchor={anchor}
            initial={reduceMotion ? false : { y: FLOAT_OFFSET_PX, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={reduceMotion ? { opacity: 0, transition: INSTANT_TRANSITION } : { y: FLOAT_OFFSET_PX, opacity: 0 }}
            transition={reduceMotion ? INSTANT_TRANSITION : FLOAT_TRANSITION}
            className="fixed z-[15000] flex flex-col overflow-hidden rounded-[14px] border border-[var(--window-border-color)] bg-[var(--window-background)]"
            style={{
              ...ticketPanelGeometry(maximized, anchor),
              ...FLOATING_SURFACE,
              transition: reduceMotion ? 'none' : RESIZE_TRANSITION,
              // Out of the picture while a screenshot or recording is taken; kept
              // mounted so the draft and the running capture survive.
              ...(capturing ? { visibility: 'hidden' as const, pointerEvents: 'none' as const } : {}),
            }}
          >
            <header className="flex shrink-0 items-center justify-between pb-3 pl-[18px] pr-3.5 pt-4">
              <h2 id="ticket-panel-heading" className="text-[17px] font-bold text-[var(--text-color)]">
                {appendTarget && view.name === 'form' ? t('ticketPanel.append.title') : t('ticketPanel.title')}
              </h2>
              <div className="flex gap-0.5">
                <button
                  type="button"
                  onClick={toggleMaximized}
                  aria-pressed={maximized}
                  aria-label={maximized ? t('notificationCenter.restore') : t('notificationCenter.maximize')}
                  title={maximized ? t('notificationCenter.restore') : t('notificationCenter.maximize')}
                  data-testid="ticket-panel-maximize"
                  className={ICON_BUTTON}
                >
                  {maximized ? <Minimize2 className="h-4 w-4" aria-hidden /> : <Maximize2 className="h-4 w-4" aria-hidden />}
                </button>
                <button
                  type="button"
                  onClick={close}
                  aria-label={t('common.close')}
                  title={t('common.close')}
                  data-testid="ticket-panel-close"
                  className={ICON_BUTTON}
                  disabled={submitting}
                >
                  <X className="h-[17px] w-[17px]" aria-hidden />
                </button>
              </div>
            </header>

            {view.name === 'success' ? (
              success
            ) : view.name === 'markup' && markupTarget?.original ? (
              <div className="flex min-h-0 flex-1 flex-col px-[18px] pb-4">
                <ScreenshotMarkup
                  key={markupTarget.id}
                  original={markupTarget.original}
                  shapes={markupTarget.shapes ?? []}
                  onApply={(shapes, marked) => applyMarkup(markupTarget.id, shapes, marked)}
                  onCancel={() => setView({ name: 'form' })}
                />
              </div>
            ) : (
              form
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}
